import {
  Accessor,
  Component,
  createEffect,
  createMemo,
  createSignal,
  For,
  JSX,
  on,
  Show,
} from 'solid-js';
import { Portal } from 'solid-js/web';
import { ModusWcSliderCustomEvent } from '@trimble-oss/moduswebcomponents';
import { type Placement } from '@floating-ui/dom';
import { Icon } from '@tcweb/connect-ui';
import {
  BasicButton,
  dropdownSurfaceShadowClass,
  fixModusSliderStyle,
  usePortalMount,
  useFloatingMenuPosition,
} from '@trimble/ui-components/solidjs';
import { useDropdown } from '@trimble/solidjs-utils';
import { t } from '@trimble/i18n';
import { UnreachableCaseError } from 'ts-essentials';
import { TransparencyRow } from './TransparencyRow';
import { BooleanRow } from './BooleanRow';
import { RangeRow } from './RangeRow';

fixModusSliderStyle();

/**
 * Link state for rows that mirror an upstream value (e.g. main-toolbar
 * global). A row is rendered "linked" by default and unlinks the moment
 * the user changes its value via the slider. Slider rows relink when the
 * user moves the matching global slider.
 */
export interface RowLinkState {
  readonly isLinked: Accessor<boolean>;
}

/**
 * Range rows have no global control to relink them, so they keep an
 * explicit relink button instead of the passive link-status indicator.
 */
interface RangeRowLinkState extends RowLinkState {
  onRelink(): void;
}

interface BaseRow {
  readonly id: string;
  readonly labelKey: string;
  readonly isDisabled?: Accessor<boolean>;
  readonly disabledTooltipKey?: string;
}

export interface SliderRowConfig extends BaseRow {
  readonly kind: 'slider';
  readonly min: number;
  readonly percent: Accessor<number>;
  readonly link?: RowLinkState;
  readonly sliderTestId?: string;
  onChange(percent: number): void;
}

export interface ToggleRowConfig extends BaseRow {
  readonly kind: 'toggle';
  readonly value: Accessor<boolean>;
  readonly switchTestId?: string;
  onChange(value: boolean): void;
}

export interface RangeRowConfig extends BaseRow {
  readonly kind: 'range';
  readonly min: number;
  readonly max: number;
  readonly step?: number;
  readonly value: Accessor<number>;
  readonly link?: RangeRowLinkState;
  readonly sliderTestId?: string;
  readonly relinkTooltipKey?: string;
  formatValue?(v: number): string;
  onChange(value: number): void;
}

export interface DividerRowConfig {
  readonly kind: 'divider';
  readonly id: string;
}

export type VisibilityRowConfig =
  | SliderRowConfig
  | ToggleRowConfig
  | RangeRowConfig
  | DividerRowConfig;

export interface VisibilityTriggerParams {
  readonly isOpen: Accessor<boolean>;
  toggle(): void;
}

export interface VisibilityControlProps {
  readonly rows: Accessor<VisibilityRowConfig[]>;
  readonly buttonSize?: 'small' | 'medium';
  readonly buttonTooltipKey?: string;
  readonly dropdownPlacement?: Placement;
  /** Align menu edge with trigger left or right; ignored when dropdownPlacement is set. */
  readonly menuAlign?: 'left' | 'right';
  readonly controlTestId?: string;
  readonly menuTestId?: string;
  readonly resetAllLabelKey?: string;
  renderTrigger?(params: VisibilityTriggerParams): JSX.Element;
  resetAll?(): void;
}

/**
 * Generic visibility dropdown. Accepts a row config array so the same
 * component renders the main-toolbar global controls and the per-pane
 * controls (which mix sliders, toggles, and range inputs in
 * mode-specific combinations).
 *
 * Slider rows cache their display percent locally between menu opens so
 * the slider stays snappy during drag; the upstream-driven value
 * (`percent` accessor) is re-synced when the menu reopens or when a
 * row's link state flips back to linked.
 */
export const VisibilityControl: Component<VisibilityControlProps> = (props) => {
  const portalMount = usePortalMount();
  const { isOpen, toggle, ref, dropdownId } = useDropdown();
  const [rowPercents, setRowPercents] = createSignal<Record<string, number>>(
    {}
  );
  let anchorRef: HTMLElement | undefined;
  const { menuStyle, opensUpward, setMenuSectionRef } = useFloatingMenuPosition(
    {
      getAnchor: () => anchorRef,
      getPosition: () => props.menuAlign,
      getPlacement: () => props.dropdownPlacement,
    }
  );

  const sliderRows = createMemo((): SliderRowConfig[] =>
    props.rows().filter((r): r is SliderRowConfig => r.kind === 'slider')
  );

  const syncSliderPercents = (): void => {
    const next: Record<string, number> = {};
    for (const row of sliderRows()) {
      next[row.id] = row.percent();
    }
    setRowPercents(next);
  };

  // Re-sync the cached slider percents whenever a slider row's link state
  // flips: a global move relinks the row, so the open menu must snap the
  // slider to the upstream value immediately.
  createEffect(
    on(
      () => sliderRows().map((r) => r.link?.isLinked?.() ?? null),
      () => {
        if (isOpen()) syncSliderPercents();
      }
    )
  );

  const handleToggle = (): void => {
    syncSliderPercents();
    toggle();
  };

  const handleSliderChange =
    (row: SliderRowConfig) =>
    (ev: ModusWcSliderCustomEvent<InputEvent>): void => {
      if (row.isDisabled?.()) return;
      const value = parseInt((ev.detail.target as HTMLInputElement).value, 10);
      if (isNaN(value)) return;
      setRowPercents((prev) => ({ ...prev, [row.id]: value }));
      row.onChange(value);
    };

  const handleRangeChange =
    (row: RangeRowConfig) =>
    (value: number): void => {
      if (row.isDisabled?.()) return;
      row.onChange(value);
    };

  const percentForSlider = (row: SliderRowConfig): number =>
    rowPercents()[row.id] ?? row.percent();

  const handleShowAll = (): void => {
    props.resetAll?.();
    syncSliderPercents();
  };

  const renderRow = (row: VisibilityRowConfig): JSX.Element => {
    switch (row.kind) {
      case 'slider':
        return (
          <TransparencyRow
            id={row.id}
            labelKey={row.labelKey}
            sliderTestId={row.sliderTestId ?? `visibility-slider-${row.id}`}
            min={row.min}
            percent={percentForSlider(row)}
            isDisabled={row.isDisabled?.() ?? false}
            disabledTooltipKey={row.disabledTooltipKey}
            isLinked={row.link?.isLinked()}
            onSliderChange={handleSliderChange(row)}
          />
        );
      case 'toggle':
        // Toggle rows are short (label + switch). The container's gap-1
        // (4px) leaves them visually cramped relative to the taller
        // slider rows above, so add 4px extra top margin → 8px effective
        // between consecutive toggles. The first toggle after the
        // sliders/toggles divider absorbs the same 8px from the
        // divider's `my-1`, so the section transition stays consistent.
        return (
          <div class="mt-1 first:mt-0">
            <BooleanRow
              id={row.id}
              labelKey={row.labelKey}
              value={row.value()}
              isDisabled={row.isDisabled?.() ?? false}
              disabledTooltipKey={row.disabledTooltipKey}
              switchTestId={row.switchTestId}
              onChange={row.onChange}
            />
          </div>
        );
      case 'range':
        return (
          <RangeRow
            id={row.id}
            labelKey={row.labelKey}
            min={row.min}
            max={row.max}
            step={row.step}
            value={row.value()}
            isDisabled={row.isDisabled?.() ?? false}
            disabledTooltipKey={row.disabledTooltipKey}
            sliderTestId={row.sliderTestId ?? `visibility-range-${row.id}`}
            formatValue={row.formatValue}
            isUnlinked={row.link ? !row.link.isLinked() : false}
            relinkTooltipKey={row.relinkTooltipKey}
            onChange={handleRangeChange(row)}
            onRelink={row.link?.onRelink}
          />
        );
      case 'divider':
        return (
          <hr
            class="my-1 border-[var(--modus-wc-color-base-200)]"
            data-testid={`visibility-menu-divider-${row.id}`}
          />
        );
      default:
        throw new UnreachableCaseError(row);
    }
  };

  return (
    <div
      class="relative"
      ref={(el) => {
        ref(el as HTMLDivElement);
        anchorRef = el;
      }}
      data-testid={props.controlTestId ?? 'visibility-control'}
    >
      <Show
        when={props.renderTrigger}
        fallback={
          <BasicButton
            iconName="tc-icon-eye-visibility-toggle"
            tooltip={t(
              props.buttonTooltipKey ?? 'mobileMapping.globalToolbar.visibility'
            )}
            action={handleToggle}
            pressed={isOpen()}
            size={props.buttonSize ?? 'small'}
          />
        }
      >
        {(render) => render()({ isOpen, toggle: handleToggle })}
      </Show>
      <Show when={isOpen()}>
        <Portal mount={portalMount()}>
          <div
            class="fixed pointer-events-auto z-[9999]"
            style={menuStyle()}
            data-dropdown-id={dropdownId}
            data-testid={props.menuTestId ?? 'visibility-menu-content'}
            ref={(el) => setMenuSectionRef(el)}
          >
            <div
              class={`flex w-max min-w-48 flex-col gap-1 rounded-sm bg-base-page p-2 ${dropdownSurfaceShadowClass(opensUpward())} select-none`}
            >
              <For each={props.rows()}>{(row) => renderRow(row)}</For>
              <Show when={props.resetAll}>
                <hr class="my-1 border-[var(--modus-wc-color-base-200)]" />
                <div
                  class="flex cursor-pointer items-center gap-2 p-2 hover:bg-[var(--modus-wc-color-base-100)]"
                  data-cy="visibility-show-all-button"
                  data-testid="visibility-show-all-button"
                  role="button"
                  tabIndex={0}
                  onClick={handleShowAll}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleShowAll();
                    }
                  }}
                  title={t(
                    props.resetAllLabelKey ??
                      'mobileMapping.transparency.showAll'
                  )}
                  aria-label={t(
                    props.resetAllLabelKey ??
                      'mobileMapping.transparency.showAll'
                  )}
                >
                  <Icon
                    icon="tc-icon-show-all"
                    class="flex min-h-5 min-w-5 text-base-content"
                  />
                  <span class="whitespace-nowrap text-sm text-base-content">
                    {t(
                      props.resetAllLabelKey ??
                        'mobileMapping.transparency.showAll'
                    )}
                  </span>
                </div>
              </Show>
            </div>
          </div>
        </Portal>
      </Show>
    </div>
  );
};
